export const ACCOUNT_TOKEN_KEY = "meucontroleai:account-token"
export const ACCOUNT_TOKEN_EXPIRES_KEY = "meucontroleai:account-token-expires"
export const PROFILE_KEY = "profile"

/** How long before expiry the storefront reissues the customer JWT. */
export const ACCOUNT_TOKEN_RENEW_LEAD_MS = 6 * 60 * 60 * 1000

/** Reads the raw account token from localStorage (null when absent). */
export function getAccountToken(): string | null {
  return localStorage.getItem(ACCOUNT_TOKEN_KEY)
}

/** Reads the stored expiry epoch in ms, or null when it is missing or not a number. */
export function getAccountTokenExpiresAt(): number | null {
  const raw = localStorage.getItem(ACCOUNT_TOKEN_EXPIRES_KEY)
  if (!raw) return null
  const expiresAt = parseInt(raw, 10)
  if (Number.isNaN(expiresAt)) return null
  return expiresAt
}

/** True when a stored token should be reissued before checkout or before it expires. */
export function accountTokenNeedsRenewal(now = Date.now()): boolean {
  if (!getAccountToken()) return false
  const expiresAt = getAccountTokenExpiresAt()
  if (expiresAt === null) return true
  return now >= expiresAt - ACCOUNT_TOKEN_RENEW_LEAD_MS
}

/** Removes the account token, its expiry and the cached profile. */
export function clearAccountToken(): void {
  localStorage.removeItem(ACCOUNT_TOKEN_KEY)
  localStorage.removeItem(ACCOUNT_TOKEN_EXPIRES_KEY)
  localStorage.removeItem(PROFILE_KEY)
}

/**
 * Returns the account token, or redirects to `redirectTo` and returns null
 * when the user is not authenticated.
 */
export function requireAccountToken(redirectTo: string): string | null {
  const accountToken = getAccountToken()

  if (!accountToken) {
    window.location.href = redirectTo
    return null
  }

  return accountToken
}

/**
 * Returns whether the user holds a non-expired account token.
 * Clears the token (and cached profile) when expired.
 * Safe to call outside React components.
 */
export function checkAuthStatus(): boolean {
  const accountToken = getAccountToken()

  if (!accountToken) {
    return false
  }

  const expiresAt = localStorage.getItem(ACCOUNT_TOKEN_EXPIRES_KEY)
  if (expiresAt) {
    const expirationTime = parseInt(expiresAt, 10)
    if (!isNaN(expirationTime) && Date.now() > expirationTime) {
      clearAccountToken()
      return false
    }
  }

  return true
}
